import ChevronRight from "@mui/icons-material/ChevronRight";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import type { ScoutToolPart } from "./message/scoutChatMessage";

import { ToolIoJsonBlock } from "./ToolIoJsonBlock";
import { renderToolTitle } from "./toolRenderers";

const SECONDARY_TEXT = "rgba(0, 0, 0, 0.6)";
const TERTIARY_TEXT = "rgba(0, 0, 0, 0.42)";
const GRAPHQL_TOOL_NAMES = new Set(["search_api", "describe_operation", "execute_graphql"]);

function isToolRunning(tool: ScoutToolPart): boolean {
  return tool.toolState === "input_streaming" || tool.toolState === "input_available";
}

/**
 * Format a delta as a compact, understated relative timestamp suitable for
 * the right-hand metadata of a tool-call row.
 *
 *   under 5s        → "just now"
 *   5s … 60s        → "12s ago"
 *   60s … 60m       → "5m ago"
 *   60m … 24h       → "2h ago"
 *   24h+            → "3d ago"
 *
 * Deliberately compact (single unit) so it lives quietly at the end of the
 * line. `date-fns` produces longer phrases ("about 2 minutes ago") that
 * crowd the row.
 */
function formatToolRelativeTime(createdAt: string | undefined, now: number): string | null {
  if (!createdAt) return null;
  const then = Date.parse(createdAt);
  if (Number.isNaN(then)) return null;
  const deltaMs = Math.max(0, now - then);
  const seconds = Math.floor(deltaMs / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Re-renders every `intervalMs` so the relative-time labels stay fresh.
 * Call this ONCE per `ToolCallGroup` and pass the clock value down to each
 * `ToolCallRow` as a prop — the hook registers a `setInterval` per call,
 * so per-row instantiation gives N timers + N tick-driven re-renders, which
 * adds up quickly across an active chat.
 */
function useTickingClock(intervalMs: number): number {
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const handle = setInterval(() => setClock(Date.now()), intervalMs);
    return () => clearInterval(handle);
  }, [intervalMs]);
  return clock;
}

export function ToolCallGroup({
  toolParts,
  isStreaming = false,
  expanded: expandedProp,
  onExpandedChange,
  showToolIoExpansion = false,
}: {
  toolParts: ScoutToolPart[];
  /**
   * Whether the owning assistant message is still streaming. We use this to
   * suppress the header flip from "checking" → "checked" → "checking" when
   * tools arrive sequentially: if the turn isn't finished yet, more tools may
   * still be coming, so stay in the "still working" state.
   */
  isStreaming?: boolean;
  expanded?: boolean;
  onExpandedChange?: (nextExpanded: boolean) => void;
  showToolIoExpansion?: boolean;
}) {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const expanded = expandedProp ?? uncontrolledExpanded;
  const setExpanded = (nextExpanded: boolean) => {
    onExpandedChange?.(nextExpanded);
    if (expandedProp === undefined) {
      setUncontrolledExpanded(nextExpanded);
    }
  };
  // Single ticking clock for every row in this group. The hook registers
  // its own `setInterval`, so calling it per row would multiply timers and
  // tick-driven re-renders across the chat.
  const clock = useTickingClock(15_000);

  if (toolParts.length === 0) return null;

  const anyRunning = toolParts.some(isToolRunning);
  const stillWorking = anyRunning || isStreaming;
  const headerContent = getHeaderText(toolParts, stillWorking);
  // When the group has multiple tools and is collapsed, surface the most
  // recent one as a single peek row underneath the header. As new tool
  // calls land, this row updates in place — gives the user a sense of
  // progress without needing to expand.
  const latestTool = toolParts.length > 1 ? toolParts[toolParts.length - 1] : null;

  return (
    <Box sx={{ mt: 1, mb: 1.75 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          color: SECONDARY_TEXT,
          "&:hover": { color: "text.primary" },
        }}
      >
        <Typography variant="body1" component="div" sx={{ fontWeight: 400, color: SECONDARY_TEXT }}>
          {headerContent}
        </Typography>
        <ChevronRight
          fontSize="small"
          sx={{
            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease-in-out",
          }}
        />
      </Stack>
      {!expanded && latestTool && (
        <Box sx={{ mt: 0.5, ml: 0 }}>
          <ToolCallRow tool={latestTool} clock={clock} />
        </Box>
      )}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack spacing={1} sx={{ mt: 0.75 }}>
          {toolParts.map((tool) => (
            <ToolCallRow
              key={tool.id}
              tool={tool}
              clock={clock}
              showToolIoExpansion={showToolIoExpansion}
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function ToolCallRow({
  tool,
  clock,
  showToolIoExpansion = false,
}: {
  tool: ScoutToolPart;
  clock: number;
  showToolIoExpansion?: boolean;
}) {
  const isError = tool.toolState === "output_error";
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  // 15s tick value comes from the parent ToolCallGroup; one timer per group
  // rather than per row.
  const relative = formatToolRelativeTime(tool.createdAt, clock);
  const hasToolInput = tool.toolInput !== undefined;
  const hasToolOutput = tool.toolOutput !== undefined;
  const hasToolError = Boolean(tool.toolError);
  const hasToolIoDetails = showToolIoExpansion && (hasToolInput || hasToolOutput || hasToolError);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ color: isError ? "error.main" : SECONDARY_TEXT }}
      >
        {hasToolIoDetails && (
          <IconButton
            aria-expanded={detailsExpanded}
            aria-label={
              detailsExpanded ? "Hide tool input and output" : "Show tool input and output"
            }
            onClick={() => setDetailsExpanded((current) => !current)}
            size="small"
            sx={{ color: "inherit", ml: -0.75, p: 0.25 }}
          >
            <ChevronRight
              fontSize="inherit"
              sx={{
                transform: detailsExpanded ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease-in-out",
              }}
            />
          </IconButton>
        )}
        {isError && <ErrorOutline fontSize="inherit" color="error" />}
        <Typography variant="body2" component="div" sx={{ flexGrow: 1, minWidth: 0 }}>
          {renderToolTitle({ tool, isRunning: isToolRunning(tool) })}
        </Typography>
        {relative && (
          <Typography
            variant="caption"
            component="span"
            sx={{
              color: TERTIARY_TEXT,
              flexShrink: 0,
              fontVariantNumeric: "tabular-nums",
              // Pin the timestamp column so the row's text doesn't shift
              // horizontally when the label flips between "12s ago" and
              // "2m ago" (different widths). The longest plausible value is
              // "999s ago" / "999d ago" — 7 chars at the caption font size.
              minWidth: "5.5em",
              textAlign: "right",
            }}
            aria-label={`Started ${relative}`}
          >
            {relative}
          </Typography>
        )}
      </Stack>
      {hasToolIoDetails && (
        <Collapse in={detailsExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ ml: 3, mt: 0.25 }}>
            <Typography color="text.secondary" sx={{ display: "block", mb: 0.5 }} variant="caption">
              <Box component="span" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
                Tool name:
              </Box>{" "}
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                {tool.toolName}
              </Box>
            </Typography>
            {hasToolInput && <ToolIoJsonBlock title="Tool input" value={tool.toolInput} />}
            {hasToolOutput && <ToolIoJsonBlock title="Tool output" value={tool.toolOutput} />}
            {hasToolError && <ToolIoJsonBlock title="Tool error" value={tool.toolError} />}
            {!hasToolOutput && isToolRunning(tool) && (
              <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
                Waiting for tool output…
              </Typography>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

function getHeaderText(toolParts: ScoutToolPart[], stillWorking: boolean) {
  if (toolParts.length === 1) {
    return renderToolTitle({ tool: toolParts[0], isRunning: isToolRunning(toolParts[0]) });
  }

  const allGraphqlTools = toolParts.every((tool) => GRAPHQL_TOOL_NAMES.has(tool.toolName));
  if (allGraphqlTools) {
    return stillWorking ? "I'm checking your Encamp data" : "Checking your Encamp data";
  }

  return stillWorking ? "I'm checking a few things" : "Checking a few things";
}
